import { createRouter, createWebHistory } from 'vue-router'
import PageNotFound from '../views/PageNotFound.vue'
import getLoading from "@/functions/getLoading"
import { resetSideDrawer } from "@/functions/getModal"
import { user } from "@/functions/user"
import { isPreparerEnabled } from "@/functions/getPreparerContext"
import { trackPageView } from '@/functions/tealium'

const { hideLoading } = getLoading()
const hostname = new URL(location.href).hostname
const queryString = window.location.search
const urlParams = new URLSearchParams(queryString)
let appName
let appTradeName
let appYear = ''
if (hostname.includes('freetaxusa')) {
	appName = 'FreeTaxUSA'
} else if (hostname.includes('taxhawk')) {
	appName = 'TaxHawk'
} else if (hostname.includes('express1040')) {
	appName = 'Express1040'
}
appTradeName = appName + '\u00AE'
sessionStorage.setItem('appName', appName)
sessionStorage.setItem('appTradeName', appTradeName)
user.appName = appName
user.appTradeName = appTradeName

// Seed sessionStorage.redirect_uri before App / Navbar mount so the preparer
// logo renders on first paint (Navbar reads it synchronously via
// isPreparerContext). URL value wins if present; otherwise default by path.
const pathname = window.location.pathname
const redirectUriFromQuery = urlParams.get('redirect_uri')

// Same-tab navigations after a preparer session retain sessionStorage. A full
// reload of the root login page with no preparer hint should reset, otherwise
// the navbar shows the preparer logo on the normal login (#400).
if ((pathname === '/' || pathname === '') && !redirectUriFromQuery) {
	sessionStorage.removeItem('redirect_uri')
}

// Don't seed a preparer redirect_uri when the feature is disabled.
if (!sessionStorage.getItem('redirect_uri')) {
	if (redirectUriFromQuery) {
		if (isPreparerEnabled() || !redirectUriFromQuery.includes('preparer')) {
			sessionStorage.setItem('redirect_uri', redirectUriFromQuery)
		}
	} else if (isPreparerEnabled() && pathname === '/preparer/link-client-account') {
		sessionStorage.setItem('redirect_uri', 'preparer_link')
	} else if (isPreparerEnabled() && pathname.startsWith('/preparer/')) {
		sessionStorage.setItem('redirect_uri', 'preparer')
	}
}

let indexPage
if (location.href.includes('NEWTAX') || sessionStorage.getItem('NEWTAX')) {
	indexPage = 'LoginNew'
} else {
	indexPage = 'Login'
}

if (urlParams.get('appYear') || sessionStorage.getItem('appYear')) {
	if (urlParams.get('appYear') && urlParams.get('appYear') >= 2015) {
		appYear = urlParams.get('appYear') + ' '
	} else if (sessionStorage.getItem('appYear')) {
		appYear = sessionStorage.getItem('appYear') + ' '
	}
}

function isPriorTaxYear (yearValue) {
	const yearParam = parseInt(yearValue, 10)
	const currentTaxYear = parseInt(process.env.VUE_APP_CURRENT_TAX_YEAR, 10)
	const minPriorTaxYear = parseInt(process.env.VUE_APP_MIN_PRIOR_TAX_YEAR, 10)
	return !isNaN(yearParam) && yearParam >= minPriorTaxYear && yearParam < currentTaxYear
}

function computeLoginCanonical (queryParams) {
	const baseUrl = 'https://auth.' + appName.toLowerCase() + '.com'
	if (isPriorTaxYear(queryParams.appYear)) {
		return baseUrl + '/?PRMPT&appYear=' + parseInt(queryParams.appYear, 10)
	}
	return baseUrl
}

// Same state-return price for every brand today; kept per-brand so they can diverge later (#418).
const stateReturnPrices = {
	FreeTaxUSA: { current: '15.99', prior: '17.99' },
	TaxHawk: { current: '15.99', prior: '17.99' },
	Express1040: { current: '15.99', prior: '17.99' },
}

function getLoginMetaDescription () {
	const base = 'Sign in to your ' + appName + ' account to file your federal taxes for free.'
	const prices = stateReturnPrices[appName]
	if (!prices) return base
	const appYearValue = urlParams.get('appYear') || sessionStorage.getItem('appYear')
	const price = isPriorTaxYear(appYearValue) ? prices.prior : prices.current
	return base + ' State Returns are $' + price + '.'
}

const routes = [
	{
		path: '/',
		name: indexPage,
		component: () => import('../views/' + indexPage),
		props: true,
		meta: {
			title: appTradeName + ' - ' + appYear + 'Sign in',
			metaTags: [
				{
					name: 'description',
					content: getLoginMetaDescription()
				},
				{
					name: 'keywords',
					content: 'keywords'
				}
			]
		},
	},
	{
		path: '/:catchAll(.*)*',
		name: "PageNotFound",
		component: PageNotFound,
	},
	{
		path: '/browser-entry-denied',
		name: 'LoginDenied',
		component: () => import('../views/LoginDenied.vue'),
		props: true,
		meta: {
			title: appTradeName + ' - Browser Entry Denied',
			metaTags: [
				{
					name: 'description',
					content: 'browser denied'
				},
				{
					name: 'keywords',
					content: 'denied'
				},
				{
					name: "robots",
					content: "noindex, nofollow",
				}
			]
		},
	},
	{
		path: '/about',
		name: 'About',
		component: () => import('../views/About.vue'),
		meta: {
			title: appTradeName + ' - About ' + appName,
		},
	},
	{
		path: '/AuthAppVerification',
		name: 'AuthAppVerification',
		component: () => import('../views/AuthAppVerification.vue'),
		props: true,
		meta: {
			title: appTradeName + ' - Authenticator App Verification',
		},
	},
	{
		path: '/confirmEmail',
		name: 'ConfirmEmail',
		component: () => import('../views/ConfirmEmail.vue'),
		props: true,
		meta: {
			title: appTradeName + ' - Confirm Email',
		},
	},
	{
		path: '/EmailVerification',
		name: 'EmailVerification',
		component: () => import('../views/EmailVerification.vue'),
		props: true,
		meta: {
			title: appTradeName + ' - Email Verification',
		},
	},
	{
		path: '/EmergencyCodeVerification',
		name: 'EmergencyCodeVerification',
		component: () => import('../views/EmergencyCodeVerification.vue'),
		props: true,
		meta: {
			title: appTradeName + ' - Emergency Code Verification',
		},
	},
	{
		path: '/TextVerification',
		name: 'TextVerification',
		component: () => import('../views/TextVerification.vue'),
		props: true,
		meta: {
			title: appTradeName + ' - Text Verification',
		},
	},
	{
		path: '/verifyWithTwoStepMethod',
		name: 'VerifyWithTwoStepMethod',
		component: () => import('../views/VerifyWithTwoStepMethod.vue'),
		props: true,
		meta: {
			title: appTradeName + ' - How to Receive Two Factor Code',
		},
	},
	{
		path: '/NewUser',
		name: 'NewUser',
		component: () => import('../views/NewUser.vue'),
		props: true,
		meta: {
			title: appTradeName + ' - New Account Setup',
		},
	},
	{
		path: '/preparer/login',
		name: 'PreparerLogin',
		component: () => import('../views/preparer/PreparerLogin.vue'),
		props: true,
		meta: {
			title: appTradeName + ' - Preparer Sign In',
		},
	},
	{
		path: '/preparer/create-preparer-account',
		name: 'CreatePreparerAccount',
		component: () => import('../views/preparer/CreatePreparerAccount.vue'),
		props: true,
		meta: {
			title: appTradeName + ' - New Preparer Account Setup',
		},
	},
	{
		path: '/preparer/verify-email',
		name: 'PreparerVerifyEmail',
		component: () => import('../views/preparer/PreparerVerifyEmail.vue'),
		props: true,
		meta: {
			title: appTradeName + ' - Verify Email',
		},
	},
	{
		path: '/preparer/create-client-account',
		name: 'CreateClientAccount',
		component: () => import('../views/preparer/CreateClientAccount.vue'),
		props: true,
		meta: {
			title: appTradeName + ' - New Client Setup',
		},
	},
	{
		path: '/preparer/link-client-account',
		name: 'LinkClientAccount',
		component: () => import('../views/preparer/LinkClientAccount.vue'),
		props: true,
		meta: {
			title: appTradeName + ' - Link Client Account',
		},
	},
	// NEW Reclaim Account
	{
		path: '/ReclaimAccount',
		name: 'ReclaimAccount',
		component: () => import('../views/ReclaimAccount/ReclaimAccount.vue'),
		props: true,
		meta: {
			title: appTradeName + ' - Reclaim your Account',
			metaTags: [
				{
					name: 'description',
					content: 'Forgot your Username or Password? No problem. Reclaim your account today.'
				}
			]
		},
	},
	{
		path: '/reclaim-with-username',
		name: 'ReclaimWithUsername',
		component: () => import('../views/ReclaimAccount/ReclaimWithUsername.vue'),
		props: true,
		meta: {
			title: appTradeName + ' - Reclaim with Username',
		},
	},
	{
		path: '/verify-ssn',
		name: 'VerifySSN',
		component: () => import('../views/ReclaimAccount/VerifySSN.vue'),
		props: true,
		meta: {
			title: appTradeName + ' - Verify SSN',
		},
	},
	{
		path: '/verify-ptin',
		name: 'VerifyPTIN',
		component: () => import('../views/ReclaimAccount/VerifyPTIN.vue'),
		props: true,
		meta: {
			title: appTradeName + ' - Verify PTIN',
		},
	},
	{
		path: '/select-account',
		name: 'SelectAccount',
		component: () => import('../views/ReclaimAccount/SelectAccount.vue'),
		props: true,
		meta: {
			title: appTradeName + ' - Select Account',
		},
	},
	{
		path: '/select-reset-method',
		name: 'SelectResetMethod',
		component: () => import('../views/ReclaimAccount/SelectResetMethod.vue'),
		props: true,
		meta: {
			title: appTradeName + ' - Select Reset Method',
		},
	},
	{
		path: '/enter-reset-code',
		name: 'EnterResetCode',
		component: () => import('../views/ReclaimAccount/EnterResetCode.vue'),
		props: true,
		meta: {
			title: appTradeName + ' - Enter Reset Code',
		},
	},
	{
		path: '/choose-new-password',
		name: 'ChooseNewPassword',
		component: () => import('../views/ReclaimAccount/ChooseNewPassword.vue'),
		props: true,
		meta: {
			title: appTradeName + ' - Choose New Password',
		},
	},
	{
		path: '/password-updated',
		name: 'PasswordUpdated',
		component: () => import('../views/ReclaimAccount/PasswordUpdated.vue'),
		props: true,
		meta: {
			title: appTradeName + ' - Password Updated',
		},
	},
	{
		path: '/enter-email',
		name: 'EnterEmail',
		component: () => import('../views/ReclaimAccount/Email.vue'),
		props: true,
		meta: {
			title: appTradeName + ' - Enter Email',
		},
	},
	{
		path: '/enter-username',
		name: 'EnterUsername',
		component: () => import('../views/ReclaimAccount/Username.vue'),
		props: true,
		meta: {
			title: appTradeName + ' - Enter Username',
		},
	},
	{
		path: '/username-email-sent',
		name: 'UsernameEmailSent',
		component: () => import('../views/ReclaimAccount/UsernameEmailSent.vue'),
		props: true,
		meta: {
			title: appTradeName + ' - Enter Email',
		},
	},
// 	Account Info
	{
		path: '/account-info',
		name: 'AccountInfo',
		component: () => import('../views/Account/AccountInfo.vue'),
		props: true,
		meta: {title: appTradeName + ' - Account Info'},
	},
	{
		path: '/account-activity',
		name: 'AccountActivity',
		component: () => import('../views/Account/AccountActivity.vue'),
		props: true,
		meta: {title: appTradeName + ' - Account Activity'},
	},
	{
		path: '/confirm-delete-mobile-phone',
		name: 'ConfirmDeleteMobilePhone',
		component: () => import('../views/Account/ConfirmDeleteMobilePhone.vue'),
		props: true,
		meta: {title: appTradeName + ' - Confirm Delete Mobile Phone'},
	},
	{
		path: '/confirm-delete-second-email',
		name: 'ConfirmDeleteSecondEmail',
		component: () => import('../views/Account/ConfirmDeleteSecondEmail.vue'),
		props: true,
		meta: {title: appTradeName + ' - Confirm Delete Second Email'},
	},
	{
		path: '/disable-auth-app-success',
		name: 'DisableAuthAppSuccess',
		component: () => import('../views/Account/DisableAuthAppSuccess.vue'),
		props: true,
		meta: {title: appTradeName + ' - Confirm Delete Second Email'},
	},
	{
		path: '/disable-emergency-codes-success',
		name: 'DisableEmergencyCodesSuccess',
		component: () => import('../views/Account/DisableEmergencyCodesSuccess.vue'),
		props: true,
		meta: {title: appTradeName + ' - Confirm Delete Second Email'},
	},
	{
		path: '/edit-email',
		name: 'EditEmail',
		component: () => import('../views/Account/EditEmail.vue'),
		props: true,
		meta: {title: appTradeName + ' - Edit Email'},
	},
	{
		path: '/edit-mobile-phone',
		name: 'EditMobilePhone',
		component: () => import('../views/Account/EditMobilePhone.vue'),
		props: true,
		meta: {title: appTradeName + ' - Edit Mobile Phone'},
	},
	{
		path: '/edit-password',
		name: 'EditPassword',
		component: () => import('../views/Account/EditPassword.vue'),
		props: true,
		meta: {title: appTradeName + ' - Edit Password'},
	},
	{
		path: '/edit-second-email',
		name: 'EditSecondEmail',
		component: () => import('../views/Account/EditSecondEmail.vue'),
		props: true,
		meta: {title: appTradeName + ' - Edit Second Email'},
	},
	{
		path: '/edit-security-settings',
		name: 'EditSecuritySettings',
		component: () => import('../views/Account/EditSecuritySettings.vue'),
		props: true,
		meta: {title: appTradeName + ' - Edit Security Settings'},
	},
	{
		path: '/edit-security-settings-password',
		name: 'EditSecuritySettingsPassword',
		component: () => import('../views/Account/EditSecuritySettingsPassword.vue'),
		props: true,
		meta: {title: appTradeName + ' - Edit Security Settings (Password)'},
	},
	{
		path: '/edit-username',
		name: 'EditUsername',
		component: () => import('../views/Account/EditUsername.vue'),
		props: true,
		meta: {title: appTradeName + ' - Edit Username'},
	},
	{
		path: '/enable-auth-app',
		name: 'EnableAuthApp',
		component: () => import('../views/Account/EnableAuthApp.vue'),
		props: true,
		meta: {title: appTradeName + ' - Enable Authenticator App'},
	},
	{
		path: '/enable-emergency-codes',
		name: 'EnableEmergencyCodes',
		component: () => import('../views/Account/EnableEmergencyCodes.vue'),
		props: true,
		meta: {title: appTradeName + ' - Enable Authenticator App'},
	},
	{
		path: '/enable-auth-app-qr-code',
		name: 'EnableAuthAppQrCode',
		component: () => import('../views/Account/EnableAuthAppQrCode.vue'),
		props: true,
		meta: {title: appTradeName + ' - Enable Auth App (QR Code)'},
	},
	{
		path: '/enable-auth-app-success',
		name: 'EnableAuthAppSuccess',
		component: () => import('../views/Account/EnableAuthAppSuccess.vue'),
		props: true,
		meta: {title: appTradeName + ' - Auth App Enabled'},
	},
	{
		path: '/enable-emergency-codes',
		name: 'EnableEmergencyCodes',
		component: () => import('../views/Account/EnableEmergencyCodes.vue'),
		props: true,
		meta: {title: appTradeName + ' - Emergency Codes'},
	},
	{
		path: '/enable-text-verification',
		name: 'EnableTextVerification',
		component: () => import('../views/Account/EnableTextVerification.vue'),
		props: true,
		meta: {title: appTradeName + ' - Enable Text Verification'},
	},
	{
		path: '/emergency-codes',
		name: 'EmergencyCodes',
		component: () => import('../views/Account/EmergencyCodes.vue'),
		props: true,
		meta: {title: appTradeName + ' - New Emergency Codes'},
	},
	{
		path: '/passkey',
		name: 'Passkey',
		component: () => import('../views/Passkey.vue'),
		meta: {
			title: appTradeName + ' - Passkey',
		},
	},
	{
		path: '/passkey-upsell',
		name: 'PasskeyUpsell',
		component: () => import('../views/PasskeyUpsell.vue'),
		meta: {
			title: appTradeName + ' - Passkey',
		},
	},
	{
		path: '/update-password',
		name: 'UpdatePassword',
		component: () => import('../views/UpdatePassword.vue'),
		meta: {
			title: appTradeName + ' - Update Password',
			metaTags: [
				{
					name: "robots",
					content: "noindex, nofollow",
				}
			]
		},
	},
	{
		path: '/verify-sms',
		name: 'VerifySMS',
		component: () => import('../views/Account/VerifySMS.vue'),
		props: true,
		meta: {title: appTradeName + ' - Verify SMS'},
	},
	{
		path: '/login-sso',
		name: 'LoginSso',
		component: () => import('../views/LoginSso.vue'),
		props: true,
		meta: {
			title: appTradeName + ' - ' + appYear + 'Sign in',
		},
	},
	{
		path: '/sso-callback',
		name: 'SsoCallback',
		component: () => import('../views/SsoCallback.vue'),
		meta: {
			title: appTradeName + ' - SSO Sign In',
			metaTags: [
				{
					name: "robots",
					content: "noindex, nofollow",
				}
			]
		},
	},
	{
		path: '/partner/:provider',
		name: 'PartnerLanding',
		component: () => import('../views/PartnerLanding.vue'),
		meta: {
			title: appTradeName + ' - Partner Import',
			metaTags: [
				{
					name: "robots",
					content: "noindex, nofollow",
				}
			]
		},
	},
	{
		path: '/choose-username',
		name: 'ChooseUsername',
		component: () => import('../views/ChooseUsername.vue'),
		meta: {
			title: appTradeName + ' - Choose Username',
			metaTags: [
				{
					name: "robots",
					content: "noindex, nofollow",
				}
			]
		},
	},
	{
		path: '/partner-transfer',
		name: 'PartnerTransfer',
		component: () => import('../views/PartnerTransfer.vue'),
		meta: {
			title: appTradeName + ' - Connect Account',
			metaTags: [
				{
					name: "robots",
					content: "noindex, nofollow",
				}
			]
		},
	},
	{
		path: '/partner-link-complete',
		name: 'PartnerLinkComplete',
		component: () => import('../views/PartnerLinkComplete.vue'),
		meta: {
			title: appTradeName + ' - Partner Account',
			metaTags: [
				{
					name: "robots",
					content: "noindex, nofollow",
				}
			]
		},
	}
]

const router = createRouter({
	history: createWebHistory(process.env.BASE_URL),
	scrollBehavior(to, from, savedPosition) {
		if (savedPosition) {
			return savedPosition
		} else {
			return {top: 0}
		}
	},
	routes
})

// This callback runs before every route change, including on page load.
router.beforeEach((to, from, next) => {
	// Block all /preparer/* routes when the feature is disabled.
	if (!isPreparerEnabled() && to.path.startsWith('/preparer')) {
		next({path: '/', replace: true})
		return
	}

	if (to.path === '/' || to.path === '') {
		const redirectUriFromQuery = new URLSearchParams(window.location.search).get('redirect_uri')
		const redirectUri = sessionStorage.getItem('redirect_uri') || redirectUriFromQuery

		if (redirectUriFromQuery && !sessionStorage.getItem('redirect_uri') &&
			(isPreparerEnabled() || !redirectUriFromQuery.includes('preparer'))) {
			sessionStorage.setItem('redirect_uri', redirectUriFromQuery)
		}

		if (isPreparerEnabled() && redirectUri === 'preparer') {
			next({name: 'PreparerLogin', query: to.query, replace: true})
			return
		}
		if (isPreparerEnabled() && redirectUri === 'preparer_link') {
			next({name: 'LinkClientAccount', query: to.query, replace: true})
			return
		}
	}

	const nearestWithTitle = to.matched.slice().reverse().find(r => r.meta && r.meta.title);
	const nearestWithMeta = to.matched.slice().reverse().find(r => r.meta && r.meta.metaTags);
	const nearestWithCanonical = to.matched.slice().reverse().find(r => r.meta && r.meta.canonical);
	const previousNearestWithMeta = from.matched.slice().reverse().find(r => r.meta && r.meta.metaTags);

	if (nearestWithTitle) {
		document.title = nearestWithTitle.meta.title;
	} else if (previousNearestWithMeta) {
		document.title = previousNearestWithMeta.meta.title;
	}

	const existingCanonical = document.head.querySelector('link[rel="canonical"]')
	if (existingCanonical) {
		existingCanonical.remove()
	}

	let canonicalHref = null
	if (to.path === '/' || to.path === '') {
		canonicalHref = computeLoginCanonical(to.query)
	} else if (nearestWithCanonical) {
		canonicalHref = nearestWithCanonical.meta.canonical
	}

	if (canonicalHref) {
		const canonical = document.createElement('link')
		canonical.setAttribute('rel', 'canonical')
		canonical.setAttribute('href', canonicalHref)
		document.head.append(canonical)
	}

	Array.from(document.querySelectorAll('[data-vue-router-controlled]')).map(el => el.parentNode.removeChild(el));

	if (!nearestWithMeta) return next();

	nearestWithMeta.meta.metaTags.map(tagDef => {
		const tag = document.createElement('meta');
		Object.keys(tagDef).forEach(key => {
			tag.setAttribute(key, tagDef[key]);
		});
		tag.setAttribute('data-vue-router-controlled', '');
		return tag;
	}).forEach(tag => document.head.appendChild(tag));

	next();
});

router.afterEach(() => {
	document.getElementById('firstTabIndex').focus()
	hideLoading()
	resetSideDrawer()
});

// Add after router creation, before export
router.afterEach((to, from) => {
	// Wait a bit for Tealium to load
	setTimeout(() => {
		trackPageView(to.name || to.path, {
			page_name: to.path,
			page_url: window.location.href,
			tealium_event: "auth_page_view"
		})
	}, 100)
})

export default router
